import type { Auth } from './type'
import { getPublicAuth } from './util'
import { getHostedUserToken } from './hosted-app'
import { useTokenStore } from './token-store'
import { AUTH, CARTO_CLIENT_ID } from '@/config/settings'

const sessionKeyStore = new Map<string, string | null>()

const AUTH0_TOKEN_KEY_SUFFIX = String(AUTH.authorizationParams?.audience ?? '')
const AUTH0_USER_KEY_SUFFIX = '@@user@@'

function getAuth0Key(suffix: string) {
  const AUTH0_STORAGE_KEY_PREFIX = `@@auth0spajs@@::${CARTO_CLIENT_ID}::${suffix}`
  if (!sessionKeyStore.has(suffix)) {
    const data = Object.keys(localStorage).find((key) =>
      key.startsWith(AUTH0_STORAGE_KEY_PREFIX),
    )

    if (!data) return null

    sessionKeyStore.set(suffix, data)
  }

  return sessionKeyStore.get(suffix) ?? null
}

/**
 * The user's SESSION JWT (Auth0), or null when there is no login. It deliberately ignores the
 * static data token: they are different credentials with different destinations (PR #20 review).
 * The Cloud Functions validate their Bearer as a CARTO 3 user JWT (JWKS plus issuer/audience plus
 * role claims) and attribute `usuario` to the persisted query. The token from
 * `carto credentials create token` is NOT a user JWT and would get a 401. For DATA surfaces
 * (catalogues, Table API) use `getAccessToken()`.
 */
export function getUserAccessToken(): Auth | null {
  // Hosted App mode: the platform-injected viewer JWT plays the session's part — same credential
  // class (a CARTO Auth0 user JWT), different transport. Set once at boot (entry.tsx), null
  // everywhere else, so standard builds fall straight through to the Auth0 localStorage read.
  const hostedToken = getHostedUserToken()
  if (hostedToken) return hostedToken

  const auth0Key = getAuth0Key(AUTH0_TOKEN_KEY_SUFFIX)
  if (!auth0Key) return null

  const response = localStorage.getItem(auth0Key)
  if (!response) return null

  try {
    const parse = JSON.parse(response) as {
      body: {
        access_token: string
      }
    }

    return parse?.body?.access_token || null
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error parsing auth token:', error)
    return null
  }
}

/**
 * Token for the DATA surfaces (catalogues through the SQL API, tiles through the Table API): the
 * front's scoped static token when it is baked in (public mode), otherwise the session JWT. To call
 * the Cloud Functions ALWAYS use `getUserAccessToken()`.
 */
export function getAccessToken(): Auth | null {
  const isAppPublic = getPublicAuth()

  if (isAppPublic) return isAppPublic

  return getUserAccessToken()
}

export function saveAccessToken(auth: Auth) {
  const isAppPublic = getPublicAuth()
  if (isAppPublic) return

  const auth0Key = getAuth0Key(AUTH0_TOKEN_KEY_SUFFIX)
  if (!auth0Key) return

  const currentAuth = localStorage.getItem(auth0Key)
  if (currentAuth) {
    try {
      const parse = JSON.parse(currentAuth) as {
        body: {
          access_token: string
        }
      }

      parse.body.access_token = auth

      localStorage.setItem(auth0Key, JSON.stringify(parse))
      // A refreshed JWT can carry a DIFFERENT roles claim; `ready` is already true at this point,
      // so this is the only signal role readers get to re-decode (see token-store.ts).
      useTokenStore.getState().bumpTokenVersion()
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error parsing auth token:', error)
    }
  }
}

export function clearAccessToken() {
  const isAppPublic = getPublicAuth()
  if (isAppPublic) return

  // With no session token the data surfaces go back to waiting (this keeps requests with an empty
  // Bearer from being fired after a logout or an expiry). Public mode never reaches this point (it
  // returns above).
  useTokenStore.getState().setReady(false)

  const auth0Key = getAuth0Key(AUTH0_TOKEN_KEY_SUFFIX)
  if (!auth0Key) return

  localStorage.removeItem(auth0Key)
  sessionKeyStore.clear()
}

export function getUserId(): Auth | null {
  const isAppPublic = getPublicAuth()

  if (isAppPublic) return null

  const auth0Key = getAuth0Key(AUTH0_USER_KEY_SUFFIX)
  if (!auth0Key) return null

  const response = localStorage.getItem(auth0Key)
  if (!response) return null

  try {
    const parse = JSON.parse(response) as {
      decodedToken: {
        user: {
          sub: string
        }
      }
    }

    return parse?.decodedToken?.user?.sub || null
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error parsing auth info:', error)
    return null
  }
}
